/**
 * Design System Tokens
 * 
 * Este arquivo centraliza todos os tokens do design system para garantir
 * consistência visual em todo o sistema.
 */

// ============================================================================
// ESPAÇAMENTOS
// ============================================================================

export const SPACING = {
  /** Padding padrão de páginas */
  page: 'p-6',
  /** Espaçamento entre seções */
  section: 'space-y-6',
  /** Espaçamento interno de cards */
  card: 'space-y-4',
  /** Gap entre elementos inline */
  inline: 'gap-2',
  /** Gap compacto */
  tight: 'gap-1.5',
  /** Gap maior para formulários */
  form: 'gap-4',
} as const;

// ============================================================================
// TIPOGRAFIA
// ============================================================================

export const TYPOGRAPHY = {
  /** Título principal de página */
  pageTitle: 'text-2xl font-bold text-foreground',
  /** Subtítulo de página */
  pageSubtitle: 'text-muted-foreground',
  /** Título de card/seção */
  cardTitle: 'text-base font-semibold text-foreground',
  /** Subtítulo de card */
  cardSubtitle: 'text-sm text-muted-foreground',
  /** Label de formulário */
  label: 'text-sm font-medium text-foreground',
  /** Texto do corpo */
  body: 'text-sm text-foreground',
  /** Texto pequeno/secundário */
  small: 'text-xs text-muted-foreground',
  /** Texto monospace (números, IDs) */
  mono: 'font-mono text-xs',
  /** Valor grande (métricas) */
  metric: 'text-2xl font-bold',
  /** Valor de moeda */
  currency: 'font-semibold tabular-nums',
} as const;

// ============================================================================
// TAMANHOS DE ÍCONES
// ============================================================================

export const ICON_SIZES = {
  /** Ícones em headers de página */
  header: 'h-6 w-6',
  /** Ícones padrão em botões */
  button: 'h-4 w-4',
  /** Ícones em cards Kanban */
  card: 'h-3.5 w-3.5',
  /** Ícones de indicadores pequenos */
  indicator: 'h-3 w-3',
  /** Ícones em empty states */
  empty: 'h-12 w-12',
  /** LEDs de status */
  led: 'h-2 w-2',
} as const;

// ============================================================================
// VARIANTES DE CARDS
// ============================================================================

export const CARD_VARIANTS = {
  /** Card padrão */
  default: 'rounded-lg border bg-card shadow-sm',
  /** Card compacto para Kanban */
  compact: 'rounded-lg border bg-card p-2 shadow-sm',
  /** Card com elevação */
  elevated: 'rounded-lg border bg-card shadow-md',
  /** Card interativo (hover) */
  interactive: 'rounded-lg border bg-card shadow-sm hover:shadow-md hover:border-primary/50 transition-all cursor-pointer',
  /** Card de métrica no dashboard */
  metric: 'rounded-lg border bg-card p-6',
} as const;

// ============================================================================
// KANBAN
// ============================================================================

export const KANBAN = {
  /** Largura da coluna */
  columnWidth: 'w-72 min-w-[288px]',
  /** Background da coluna */
  columnBg: 'bg-muted/30',
  /** Border radius da coluna */
  columnRadius: 'rounded-xl',
  /** Padding do header */
  headerPadding: 'px-3 py-2',
  /** Gap entre cards */
  cardGap: 'space-y-2',
  /** Altura do card compacto */
  cardHeight: 'min-h-[52px]',
} as const;

// ============================================================================
// BOTÕES
// ============================================================================

export const BUTTON_ICON_GAP = {
  /** Espaçamento padrão entre ícone e texto */
  default: 'mr-2',
  /** Espaçamento compacto */
  compact: 'mr-1.5',
  /** Espaçamento mínimo */
  tight: 'mr-1',
} as const;

// ============================================================================
// FORMULÁRIOS
// ============================================================================

export const FORM = {
  /** Espaçamento entre campos */
  fieldGap: 'space-y-4',
  /** Espaçamento entre seções */
  sectionGap: 'space-y-6',
  /** Grid de 2 colunas */
  grid2: 'grid grid-cols-1 md:grid-cols-2 gap-4',
  /** Grid de 3 colunas */
  grid3: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',
  /** Label padrão */
  label: 'text-sm font-medium mb-1.5 block',
  /** Descrição de campo */
  description: 'text-xs text-muted-foreground mt-1',
} as const;

// ============================================================================
// TABELAS
// ============================================================================

export const TABLE = {
  /** Container da tabela */
  container: 'rounded-lg border bg-card overflow-hidden',
  /** Header da tabela */
  header: 'bg-muted/50',
  /** Linha clicável */
  rowInteractive: 'cursor-pointer hover:bg-muted/50 transition-colors',
  /** Célula numérica */
  cellNumeric: 'text-right font-mono tabular-nums',
} as const;

// ============================================================================
// EMPTY STATES
// ============================================================================

export const EMPTY_STATE = {
  /** Container do empty state */
  container: 'flex flex-col items-center justify-center py-12 text-center',
  /** Container do ícone */
  iconContainer: 'mb-4 rounded-full bg-muted p-4 text-muted-foreground',
  /** Título */
  title: 'text-lg font-semibold text-foreground',
  /** Descrição */
  description: 'mt-1 max-w-sm text-sm text-muted-foreground',
  /** Container da ação */
  action: 'mt-4',
} as const;

// ============================================================================
// LOADING STATES
// ============================================================================

export const LOADING = {
  /** Spinner grande (full page) */
  spinnerLarge: 'h-8 w-8 animate-spin text-muted-foreground',
  /** Spinner médio */
  spinnerMedium: 'h-6 w-6 animate-spin text-muted-foreground',
  /** Spinner pequeno (inline) */
  spinnerSmall: 'h-4 w-4 animate-spin',
  /** Container centralizado */
  center: 'flex items-center justify-center',
} as const;

// ============================================================================
// ANIMAÇÕES
// ============================================================================

export const ANIMATIONS = {
  /** Transição padrão */
  transition: 'transition-all duration-200',
  /** Transição rápida */
  transitionFast: 'transition-all duration-150',
  /** Pulse para LEDs ativos */
  pulse: 'animate-pulse',
} as const;

// ============================================================================
// GRIDS RESPONSIVOS
// ============================================================================

export const GRID = {
  /** Dashboard cards (4 colunas) */
  dashboard: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4',
  /** Lista de cards (3 colunas) */
  cards: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',
  /** Grid de 2 colunas */
  two: 'grid grid-cols-1 md:grid-cols-2 gap-4',
} as const;
